import axiosInstance from "../../../api/axiosConfig";
import { APIError, handleAPIError } from "../../../api/utils/errors";
import type { ForeignNationalDocument } from "../../../api/types/foreignNationalDocument";
import type {
  CaseTaxonomy,
  DocumentRole,
  ExhibitScheme,
  FileCard,
  FsNode,
  PacketManifest
} from "../types";

/** What import does when a file of that name is already in the target folder. */
export type ConflictPolicy = "keep_both" | "replace" | "skip";

export type ImportManifestEntry = {
  relative_path: string;
  storage_path?: string;
  pdf_storage_path?: string;
  mime_type?: string;
  size_bytes?: number;
  checksum?: string;
  node_id?: string;
  on_conflict?: ConflictPolicy;
};

export type StagingCheckItem = {
  sha256: string | null;
  name: string;
  size_bytes: number;
  parent_id: string | null;
};

/** A live node somewhere in the case that a staged file matches. */
export type StagingMatch = {
  node_id: string;
  name: string;
  /** Full tree path, e.g. `/Exhibits/B — Passport.pdf`. */
  path: string;
  exhibit_label?: string | null;
  match: "exact" | "name_size";
};

export type StagingCheckResult = {
  index: number;
  /** Exact hash matches anywhere in the case (current bytes or a version). */
  duplicates: StagingMatch[];
  /** Indexes of other items in the same request with the same hash. */
  batch_duplicates: number[];
  /** Same name and size elsewhere but a different or missing checksum. */
  possible_duplicates: StagingMatch[];
  /**
   * A node with this title under the target folder; needs a decision. Titles
   * collide whatever the format, so `passport.docx` conflicts with a PDF
   * titled "passport".
   */
  name_conflict: {
    node_id: string;
    /** The existing node's title. */
    name: string;
    /** Its `title.ext`, for telling two formats apart. */
    filename?: string;
    path: string;
    kind: "file" | "folder";
  } | null;
};

export type StagingUploadRequest = {
  /** Path relative to the drop root, e.g. `"Awards/2024/prize.pdf"`. */
  relative_path: string;
  content_type?: string;
  size_bytes?: number;
};

export type StagingUploadSession = {
  /** GCS resumable-upload session URI; PUT the bytes here with bare axios. */
  upload_url: string;
  /** Where the bytes will sit in staging, to pass to `/fs/import`. */
  storage_path: string;
};

/** Which tree a zip is of: Case Files or Work Products. */
export type ZipTree = "files" | "work";

export type ZipScope = "all" | "selected" | "folder";

export type ZipRequest = {
  scope: ZipScope;
  node_ids?: string[];
  folder_id?: string | null;
  keep_structure?: boolean;
  /** Case Files: `[B.2] Pay Stubs.pdf` (on by default). */
  prefix_labels?: boolean;
  /** Work Products: drafts as DOCX (default) or PDF. */
  draft_format?: "docx" | "pdf";
  /** Work Products: the filing packet (on by default). */
  include_packet?: boolean;
};

/** A zip too large to stream, built on a worker; poll until `ready`. */
export type ZipJob = {
  job_id: string;
  status: "processing" | "ready" | "failed" | "canceled";
  done: number;
  total: number;
  download_name: string;
  /** Signed, valid for an hour once `ready`. */
  download_url?: string | null;
  expires_at?: string | null;
  error?: string | null;
};

export type ZipResult =
  | { kind: "file"; blob: Blob; filename: string }
  | { kind: "job"; job: ZipJob };

/** The work products that can be copied into Case Files. */
export type PromoteSource = { kind: "draft" | "form"; id: string };

export type PromoteRequest = {
  source: PromoteSource;
  parent_id: string | null;
  title?: string;
  format: "pdf" | "docx";
  include: boolean;
};

const filenameFrom = (disposition: string | undefined, fallback: string) => {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return disposition?.match(/filename="?([^";]+)"?/)?.[1] ?? fallback;
};

const call = async <T>(operation: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const response = await operation();
    return response.data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

export type AutocategorizeRunStatus =
  | "running"
  | "proposed"
  | "applying"
  | "applied"
  | "failed"
  | "canceled";

/**
 * One AI-organizer run; `plan` is present once `status` is `proposed`, and
 * `moves` / `needs_review` once it is `applied` (older backends send neither).
 */
export type AutocategorizeRunRecord = {
  id: string;
  status: AutocategorizeRunStatus;
  error?: string | null;
  plan?: {
    folders: AutocategorizePlannedFolder[];
    placements: AutocategorizePlacement[];
    unplaced?: string[];
    rationale?: string;
  } | null;
  moves?: AutocategorizeMove[];
  needs_review?: AutocategorizeReview[];
};

/** A file whose folder an applied run changed; paths join folder names with " / ", "" being the top level. */
export type AutocategorizeMove = {
  node_id: string;
  /** The upload name. */
  name: string;
  from_path: string;
  to_path: string;
};

export type AutocategorizeReview = {
  node_id: string;
  name: string;
  reason: string;
};

export type AutocategorizePlannedFolder = {
  temp_id: string;
  name: string;
  parent_temp_id?: string | null;
};

export type AutocategorizePlacement = {
  node_id: string;
  target_folder_temp_id?: string | null;
  target_folder_id?: string | null;
  order?: number;
  category?: string | null;
  document_type?: string | null;
  title?: string | null;
};

/** One drafted item of a PDF edit batch: `ok` items are what applying it writes. */
export type PdfEditBatchItem = {
  node_id: string;
  status: "ok" | "failed" | "skipped";
  title?: string | null;
  exhibit_label?: string | null;
  page?: number | null;
  text?: string | null;
  phrase?: string | null;
  detail?: string | null;
};

/** A draft batch from `edit_pdf(dry_run)` or `draft_pdf_changes`, before `edit_pdf(batch_id)`. */
export type PdfEditBatch = {
  id: string;
  status: "running" | "draft" | "applied" | "failed";
  target: string;
  kind?: "divider_text" | "highlight" | null;
  instruction?: string | null;
  source?: string | null;
  items: PdfEditBatchItem[];
  counts: Record<string, number>;
  error?: string | null;
};

/** One deterministic PDF edit; mirrors `pdf_file_edit_service` on the server. */
export type PdfEditOp =
  | { op: "delete_pages"; pages: string }
  | { op: "keep_pages"; pages: string }
  | { op: "rotate"; degrees: number; pages?: string | null }
  | {
      op: "insert_from_node";
      node_id: string;
      at?: number | null;
      pages?: string | null;
    }
  | { op: "annotate"; annotations: Record<string, unknown>[] }
  | {
      op: "set_annotation_contents";
      xref: number;
      contents: string;
      author?: string | null;
    }
  | { op: "remove_annotations"; xrefs?: number[] | null; pages?: string | null }
  | {
      op: "redact";
      search?: string[] | null;
      rects?: Record<string, number>[] | null;
      pages?: string | null;
      fill?: string;
    }
  | {
      op: "stamp";
      position?: string;
      template?: string;
      prefix?: string;
      start?: number;
      pad?: number;
      first_page?: number;
      skip_pages?: number[];
      font_size?: number | null;
      color?: string | null;
    }
  | {
      op: "watermark";
      text: string;
      pages?: string | null;
      opacity?: number;
      font_size?: number;
      angle?: number;
      color?: string;
    };

export const FilesystemAPI = {
  getTree(individualId: string) {
    return call<{ nodes: FsNode[]; exhibit_scheme: ExhibitScheme }>(() =>
      axiosInstance.get(`/fs/${individualId}/tree`)
    );
  },

  /** A running run is reconciled against the agent runtime on every read. */
  getAutocategorizeRun(individualId: string, runId: string) {
    return call<{ run: AutocategorizeRunRecord }>(() =>
      axiosInstance.get(`/fs/${individualId}/autocategorize/${runId}`)
    );
  },

  /** Apply a proposed organizer plan: create its folders and move the files. */
  applyAutocategorizeRun(individualId: string, runId: string) {
    return call<{ run: AutocategorizeRunRecord }>(() =>
      axiosInstance.post(`/fs/${individualId}/autocategorize/${runId}/apply`)
    );
  },

  /** Reject a proposed organizer plan; nothing moves. */
  cancelAutocategorizeRun(individualId: string, runId: string) {
    return call<{ run: AutocategorizeRunRecord }>(() =>
      axiosInstance.post(`/fs/${individualId}/autocategorize/${runId}/cancel`)
    );
  },

  createFolder(individualId: string, name: string, parentId?: string | null) {
    return call<{ node: FsNode }>(() =>
      axiosInstance.post(`/fs/${individualId}/folder`, {
        name,
        parent_id: parentId ?? null
      })
    );
  },

  patchNode(
    individualId: string,
    nodeId: string,
    updates: Partial<
      Pick<FsNode, "name" | "packet_inclusion" | "exhibit_numbering">
    >
  ) {
    return call<{ node: FsNode }>(() =>
      axiosInstance.patch(`/fs/${individualId}/node/${nodeId}`, updates)
    );
  },

  setDocumentRole(
    individualId: string,
    nodeIds: string[],
    role: DocumentRole,
    reason?: string | null
  ) {
    return call<{ nodes: FsNode[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/nodes/role`, {
        node_ids: nodeIds,
        role,
        reason: reason ?? null
      })
    );
  },

  getTaxonomy(individualId: string) {
    return call<CaseTaxonomy>(() =>
      axiosInstance.get(`/fs/${individualId}/taxonomy`)
    );
  },

  /** Sets files' category and type by hand; a null category clears both. */
  setClassification(
    individualId: string,
    nodeIds: string[],
    category: string | null,
    documentType: string | null
  ) {
    return call<{ nodes: FsNode[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/nodes/classification`, {
        node_ids: nodeIds,
        category,
        document_type: category ? documentType : null
      })
    );
  },

  moveNodes(
    individualId: string,
    nodeIds: string[],
    targetParentId: string | null,
    position?: { beforeId?: string | null; afterId?: string | null }
  ) {
    return call<{ nodes: FsNode[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/move`, {
        node_ids: nodeIds,
        target_parent_id: targetParentId,
        before_id: position?.beforeId ?? null,
        after_id: position?.afterId ?? null
      })
    );
  },

  deleteNodes(individualId: string, nodeIds: string[]) {
    return call<{ deleted: number }>(() =>
      axiosInstance.post(`/fs/${individualId}/delete`, { node_ids: nodeIds })
    );
  },

  putExhibitScheme(individualId: string, scheme: ExhibitScheme) {
    return call<{
      exhibit_scheme: ExhibitScheme;
      labels: Record<string, string | null>;
    }>(() => axiosInstance.put(`/fs/${individualId}/exhibit-scheme`, scheme));
  },

  /** Creates folder and file nodes for an uploaded tree, in manifest order. */
  importManifest(
    individualId: string,
    entries: ImportManifestEntry[],
    parentId?: string | null
  ) {
    return call<{ nodes: FsNode[]; created: number }>(() =>
      axiosInstance.post("/fs/import", {
        individual_id: individualId,
        entries,
        parent_id: parentId ?? null
      })
    );
  },

  /**
   * Save the PDF editor's output as the file's new version. `rotations` maps a
   * 1-based page of the uploaded PDF to degrees and is applied server-side (the
   * browser editor only previews a turn), so one Save is one version.
   * `baseChecksum` turns a concurrent change into a 409 rather than a silent
   * overwrite. Case files only: the server refuses the packet and other work
   * products.
   */
  replaceNodePdf(
    individualId: string,
    nodeId: string,
    pdf: Blob,
    options: {
      rotations?: Record<number, number>;
      note?: string;
      baseChecksum?: string | null;
    } = {}
  ) {
    const form = new FormData();
    form.append("file", pdf, "edited.pdf");
    const rotations = Object.fromEntries(
      Object.entries(options.rotations ?? {}).filter(
        ([, degrees]) => degrees % 360 !== 0
      )
    );
    if (Object.keys(rotations).length) {
      form.append("rotations", JSON.stringify(rotations));
    }
    if (options.note) form.append("note", options.note);
    if (options.baseChecksum)
      form.append("base_checksum", options.baseChecksum);
    return call<{ node: FsNode }>(() =>
      axiosInstance.put(`/fs/${individualId}/node/${nodeId}/pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    );
  },

  /**
   * Apply deterministic edits server-side (the same op schema the case
   * assistant's PDF tools use). `page_structure_changed` is the cue to warn
   * that draft citations pinned to a page of this file may have shifted.
   */
  applyPdfEdits(
    individualId: string,
    nodeId: string,
    ops: PdfEditOp[],
    options: { note?: string; baseChecksum?: string | null } = {}
  ) {
    return call<{
      node: FsNode;
      results: Record<string, unknown>[];
      page_structure_changed: boolean;
    }>(() =>
      axiosInstance.post(`/fs/${individualId}/node/${nodeId}/pdf/edits`, {
        ops,
        note: options.note ?? null,
        base_checksum: options.baseChecksum ?? null
      })
    );
  },

  /** A draft PDF edit batch with every item, for the chat card to list. */
  getPdfEditBatch(individualId: string, batchId: string) {
    return call<PdfEditBatch>(() =>
      axiosInstance.get(`/fs/${individualId}/pdf-edit-batches/${batchId}`)
    );
  },

  /**
   * Save a Word/Excel/PowerPoint editor's output as the file's new version
   * (`PUT /fs/.../bytes`). The old bytes go onto `versions`; the rendition and
   * text are remade off the request. `baseChecksum` is the checksum the
   * editor loaded: a 409 means the file changed underneath it.
   */
  replaceNodeBytes(
    individualId: string,
    nodeId: string,
    file: Blob,
    options: {
      filename?: string;
      note?: string;
      baseChecksum?: string | null;
    } = {}
  ) {
    const form = new FormData();
    form.append("file", file, options.filename ?? "edited");
    if (options.note) form.append("note", options.note);
    if (options.baseChecksum)
      form.append("base_checksum", options.baseChecksum);
    return call<{ node: FsNode }>(() =>
      axiosInstance.put(`/fs/${individualId}/node/${nodeId}/bytes`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    );
  },

  /** Bring `node.versions[index]` back as the current bytes (itself undoable). */
  /**
   * Duplicate / conflict signals for files the upload modal has staged,
   * compared against every live file in the case.
   */
  checkStaging(individualId: string, items: StagingCheckItem[]) {
    return call<{ results: StagingCheckResult[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/staging/check`, { items })
    );
  },

  /**
   * Open a GCS resumable-upload session for one file the modal is staging.
   * The API decides the object name under `fs-staging/{ind}/{session}/` and
   * returns the session URI the browser PUTs the bytes to directly, so file
   * size is not bounded by the API's request cap.
   */
  createStagingUpload(
    individualId: string,
    sessionId: string,
    body: StagingUploadRequest
  ) {
    return call<StagingUploadSession>(() =>
      axiosInstance.post(`/fs/${individualId}/staging/${sessionId}/files`, body)
    );
  },

  /** Best-effort removal of a cancelled upload session's staged blobs. */
  deleteStagingSession(individualId: string, sessionId: string) {
    return call<{ deleted: number }>(() =>
      axiosInstance.delete(`/fs/${individualId}/staging/${sessionId}`)
    );
  },

  /**
   * The file as a PDF, converted on the server the first time it is asked for
   * and cached on the node (`GET /fs/.../pdf`). A PDF comes back as is; an
   * image is wrapped losslessly; Office files are converted. The Case IDE uses
   * it for the legacy-Office preview, "annotate as PDF" on an image, and
   * inserting a case file's pages into a PDF. Rejects with a 415 for a type
   * that cannot become a PDF.
   */
  getNodePdf(individualId: string, nodeId: string) {
    return call<Blob>(() =>
      axiosInstance.get(`/fs/${individualId}/node/${nodeId}/pdf`, {
        responseType: "blob"
      })
    );
  },

  /** The file's card and where it stands. */
  getFileCard(individualId: string, nodeId: string) {
    return call<FileCard>(() =>
      axiosInstance.get(`/fs/${individualId}/node/${nodeId}/card`)
    );
  },

  /**
   * Build the file's card on a worker unless it has one or one is being
   * built; returns where it stands after (`queued` once started).
   */
  requestFileCard(individualId: string, nodeId: string) {
    return call<FileCard>(() =>
      axiosInstance.post(`/fs/${individualId}/node/${nodeId}/card`)
    );
  },

  /**
   * Download a tree, a selection or a folder as one zip. Small archives
   * stream back as the file; large ones answer 202 with a job to poll with
   * `zipStatus`. `onProgress` gets the bytes received so far.
   */
  async zip(
    individualId: string,
    tree: ZipTree,
    body: ZipRequest,
    options: {
      signal?: AbortSignal;
      onProgress?: (loaded: number) => void;
    } = {}
  ): Promise<ZipResult> {
    const path =
      tree === "files" ? `/fs/${individualId}/zip` : `/ws/${individualId}/zip`;
    try {
      const response = await axiosInstance.post<Blob>(path, body, {
        responseType: "blob",
        signal: options.signal,
        onDownloadProgress: (event) => options.onProgress?.(event.loaded)
      });
      if (response.status === 202) {
        const parsed = JSON.parse(await response.data.text()) as {
          job: ZipJob;
        };
        return { kind: "job", job: parsed.job };
      }
      return {
        kind: "file",
        blob: response.data,
        filename: filenameFrom(
          response.headers["content-disposition"] as string | undefined,
          tree === "files" ? "Case Files.zip" : "Work Products.zip"
        )
      };
    } catch (error) {
      const data = (error as { response?: { data?: unknown } }).response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        let message = text;
        try {
          message = (JSON.parse(text) as { error?: string }).error ?? text;
        } catch {
          // Not JSON: keep the text.
        }
        throw new APIError(
          message,
          (error as { response?: { status?: number } }).response?.status ?? 0
        );
      }
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** A zip job of either tree: `done` of `total`, then `download_url`. */
  zipStatus(individualId: string, jobId: string) {
    return call<{ job: ZipJob }>(() =>
      axiosInstance.get(`/fs/${individualId}/zip/${jobId}`)
    );
  },

  cancelZip(individualId: string, jobId: string) {
    return call<{ job: ZipJob }>(() =>
      axiosInstance.delete(`/fs/${individualId}/zip/${jobId}`)
    );
  },

  /**
   * Copy a draft or form into Case Files as a snapshot ("Add to Case Files").
   * `unresolved_citations` counts citations to exhibits no longer in the packet.
   */
  promote(individualId: string, body: PromoteRequest) {
    return call<{
      node: FsNode;
      exhibit_label: string | null;
      unresolved_citations: number;
    }>(() => axiosInstance.post(`/fs/${individualId}/promote`, body));
  },

  /** The case's client's documents; `foreign_national_id` is null without a client. */
  listClientDocuments(individualId: string) {
    return call<{
      foreign_national_id: string | null;
      documents: ForeignNationalDocument[];
    }>(() => axiosInstance.get(`/fs/${individualId}/client-documents`));
  },

  /**
   * Copy the client's documents into `parentId` (or a "From client" folder at
   * the root). The client keeps their copies.
   */
  importFromClient(
    individualId: string,
    documentIds: string[],
    parentId: string | null
  ) {
    return call<{ nodes: FsNode[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/import-from-client`, {
        document_ids: documentIds,
        ...(parentId ? { parent_id: parentId } : {})
      })
    );
  },

  /**
   * Copy files from another of the client's cases into `parentId` (or "From
   * client" at the root). Each is a new file here; the original stays.
   */
  importFromCase(
    individualId: string,
    fromIndividualId: string,
    nodeIds: string[],
    parentId: string | null
  ) {
    return call<{ nodes: FsNode[] }>(() =>
      axiosInstance.post(`/fs/${individualId}/import-from-case`, {
        from_individual_id: fromIndividualId,
        node_ids: nodeIds,
        ...(parentId ? { parent_id: parentId } : {})
      })
    );
  },

  /**
   * Bring a copy level with its source in place (label and packet position
   * kept), or with `keep` clear Draft Changed and keep the copy as it is.
   */
  updateCopy(
    individualId: string,
    nodeId: string,
    options: { keep?: boolean } = {}
  ) {
    return call<{ node: FsNode; unresolved_citations: number }>(() =>
      axiosInstance.post(`/fs/${individualId}/node/${nodeId}/update-copy`, {
        keep: options.keep ?? false
      })
    );
  },

  restoreVersion(individualId: string, nodeId: string, index: number) {
    return call<{ node: FsNode }>(() =>
      axiosInstance.post(`/fs/${individualId}/node/${nodeId}/restore-version`, {
        index
      })
    );
  },

  /**
   * The page map for a built packet. `is_stale` means the filesystem has moved
   * on since the PDF was built, so Packet Mode should say so rather than
   * highlight the wrong exhibit.
   */
  getPacketManifest(packetId: string) {
    return call<{ manifest: PacketManifest; is_stale: boolean }>(() =>
      axiosInstance.get(`/packet/${packetId}/manifest`)
    );
  }
};
